import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import type { PermissionKey, PermissionSetDto } from '@gc/shared';

import { api } from '@/lib/api';
import { Field, ghostPillClass, inputClass, pillClass } from './controls';
import { PermissionMatrix } from './PermissionMatrix';
import { PERMISSION_SETS_PATH, snapshotTemplate } from './roleTemplates';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * True when the drawer's form no longer matches the LOADED role. Apply posts
 * to `/apply`, and the server re-syncs members from the STORED template — so
 * pressing it over unsaved edits would silently push permissions the screen
 * no longer shows (an unticked box would report success and re-grant, R4#2).
 * A dirty form therefore disables Apply until the role is saved. Pure and
 * exported for the node-only pin.
 */
export function isRoleFormDirty(
  role: Pick<PermissionSetDto, 'name' | 'description' | 'permissions'>,
  form: { name: string; description: string; granted: ReadonlySet<PermissionKey> },
): boolean {
  if (form.name !== role.name) return true;
  // The submit path trims and stores blank as null; compare what WOULD be saved.
  if (form.description.trim() !== (role.description ?? '')) return true;

  // Compare against the same snapshot the drawer was seeded from, so keys the
  // catalogue dropped on read cannot make a pristine form read as dirty.
  const stored = snapshotTemplate(role);
  if (stored.size !== form.granted.size) return true;
  for (const key of form.granted) {
    if (!stored.has(key)) return true;
  }
  return false;
}

/**
 * Role templates — /settings/roles (wave 3, SPEC §4 "Employee
 * (+PermissionSet)").
 *
 * A template is a reusable bundle of §3 privileges ("Cashier", "Floor
 * staff"). Assigning one in the employee drawer SNAPSHOTS its permissions;
 * editing a template here changes NOBODY until the explicit "Apply to N
 * employees" action — which is why that button demands the role's name typed
 * back before it will fire.
 */
export function RolesPage() {
  const { t } = useTranslation();
  const { data, loading, error, reload } = useResource<PermissionSetDto[]>(PERMISSION_SETS_PATH);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PermissionSetDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  async function handleDelete(role: PermissionSetDto): Promise<void> {
    if (!window.confirm(t('settings.roles.confirmDelete', { name: role.name }))) return;
    setPageError(null);
    setNotice(null);
    try {
      await api.delete(`${PERMISSION_SETS_PATH}/${role.id}`);
      await reload();
    } catch (caught) {
      setPageError(toFieldErrors(caught, t('settings.roles.deleteFailed'))[0]?.message ?? null);
    }
  }

  return (
    <>
      <RolesBody
        roles={data}
        loading={loading}
        loadError={error}
        notice={notice}
        pageError={pageError}
        onAdd={() => setCreating(true)}
        onEdit={(role) => setEditing(role)}
        onDelete={(role) => void handleDelete(role)}
      />

      {creating ? (
        <RoleDrawer
          role={null}
          onClose={() => setCreating(false)}
          onSaved={() => void reload()}
          onApplied={() => {}}
        />
      ) : null}

      {editing ? (
        <RoleDrawer
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
          onApplied={(count) => {
            setNotice(t('settings.roles.applyDone', { count }));
            void reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Presentational body, exported for the static-markup render tests. */
export interface RolesBodyProps {
  roles: PermissionSetDto[] | null;
  loading: boolean;
  loadError: string | null;
  notice: string | null;
  pageError: string | null;
  onAdd: () => void;
  onEdit: (role: PermissionSetDto) => void;
  onDelete: (role: PermissionSetDto) => void;
}

export function RolesBody({
  roles,
  loading,
  loadError,
  notice,
  pageError,
  onAdd,
  onEdit,
  onDelete,
}: RolesBodyProps) {
  const { t } = useTranslation();

  return (
    <div className="p-6">
      <header className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.roles.title')}</h1>
        <div className="flex-1" />
        <button type="button" onClick={onAdd} className={pillClass}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.roles.addNew')}
          </span>
        </button>
      </header>

      <p className="mb-4 max-w-3xl text-sm text-content-muted">{t('settings.roles.explain')}</p>

      {loadError ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {loadError}
        </p>
      ) : null}
      {pageError ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {pageError}
        </p>
      ) : null}
      {notice ? <p className="mb-3 text-sm text-success">{notice}</p> : null}

      {loadError ? null : (
        <div className="overflow-x-auto rounded-card border border-subtle">
          <table className="w-full min-w-[44rem] max-md:min-w-0 border-collapse text-sm">
            <thead>
              <tr className="bg-raised text-content-secondary">
                <Th>{t('common.name')}</Th>
                <Th>{t('settings.roles.description')}</Th>
                {/* Counts are secondary and both visible in the Edit dialog;
                    on a phone they are what forced the sideways scroll. */}
                <Th className="max-md:hidden">{t('settings.roles.permissionCount')}</Th>
                <Th className="max-md:hidden">{t('settings.roles.memberCount')}</Th>
                <Th>{t('common.edit')}</Th>
                <Th>{t('common.delete')}</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (roles ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-content-muted">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : (roles ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-content-muted">
                    {t('settings.roles.noRoles')}
                  </td>
                </tr>
              ) : (
                (roles ?? []).map((role, index) => (
                  <tr
                    key={role.id}
                    className={index % 2 === 0 ? 'bg-[var(--row-even)]' : 'bg-[var(--row-odd)]'}
                  >
                    <Td>
                      <span className="text-content">{role.name}</span>
                    </Td>
                    <Td>
                      <span className="text-content-secondary">{role.description ?? '—'}</span>
                    </Td>
                    <Td className="max-md:hidden">
                      <span className="tabular text-content-secondary">
                        {role.permissions.length}
                      </span>
                    </Td>
                    <Td className="max-md:hidden">
                      <span className="inline-flex items-center gap-1 tabular text-content-secondary">
                        <Users className="h-4 w-4" aria-hidden />
                        {role.memberCount}
                      </span>
                    </Td>
                    <Td>
                      <IconButton label={t('common.edit')} onClick={() => onEdit(role)}>
                        <Pencil className="h-4 w-4" aria-hidden />
                      </IconButton>
                    </Td>
                    <Td>
                      <IconButton label={t('common.delete')} danger onClick={() => onDelete(role)}>
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </IconButton>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Create/edit drawer, reusing the shared PermissionMatrix so a role edits with
 * exactly the checkboxes an employee does.
 *
 * The APPLY control lives here, on the edit form, because that is where the
 * divergence is visible: you just changed the template, and N employees still
 * carry the old snapshot. It is deliberately heavyweight — type the role's
 * name to arm it — since one click rewrites N matrices at once.
 */
function RoleDrawer({
  role,
  onClose,
  onSaved,
  onApplied,
}: {
  role: PermissionSetDto | null;
  onClose: () => void;
  onSaved: () => void;
  onApplied: (count: number) => void;
}) {
  const { t } = useTranslation();
  const isEdit = role !== null;

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [granted, setGranted] = useState<Set<PermissionKey>>(
    role ? snapshotTemplate(role) : new Set(),
  );

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // The typed confirm for Apply. Blank = disarmed — and so is a DIRTY form:
  // apply pushes the SAVED template, so unsaved edits must be saved first.
  const [applyText, setApplyText] = useState('');
  const [applying, setApplying] = useState(false);
  const applyDirty =
    isEdit && role !== null && isRoleFormDirty(role, { name, description, granted });
  const applyArmed = isEdit && !applyDirty && applyText.trim() === (role?.name ?? '');

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);
    try {
      const payload = {
        name,
        ...(description.trim()
          ? { description: description.trim() }
          : isEdit
            ? { description: null }
            : {}),
        permissions: [...granted],
      };
      if (isEdit && role) {
        await api.patch(`${PERMISSION_SETS_PATH}/${role.id}`, payload);
      } else {
        await api.post(PERMISSION_SETS_PATH, payload);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.roles.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApply(): Promise<void> {
    if (!role || !applyArmed) return;
    setErrors([]);
    setApplying(true);
    try {
      const result = await api.post<{ appliedCount: number }>(
        `${PERMISSION_SETS_PATH}/${role.id}/apply`,
        {},
      );
      onApplied(result.appliedCount);
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.roles.applyFailed')));
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-2xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('settings.roles.editRole') : t('settings.roles.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('common.name')} error={errorFor('name')}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                className={inputClass}
              />
            </Field>

            <Field label={t('settings.roles.description')} error={errorFor('description')}>
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>

          <section className="mt-6">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-content-secondary">
              {t('settings.employees.privileges')}
            </h3>
            {errorFor('permissions') ? (
              <p className="mb-2 text-xs text-danger">{errorFor('permissions')}</p>
            ) : null}
            <PermissionMatrix granted={granted} onChange={setGranted} />
          </section>

          {isEdit && role ? (
            <section className="mt-6 rounded-card border border-danger/40 bg-input p-4">
              <h3 className="text-sm font-semibold text-content">
                {t('settings.roles.applyTitle')}
              </h3>
              {role.memberCount === 0 ? (
                <p className="mt-1 text-sm text-content-muted">
                  {t('settings.roles.applyNoMembers')}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-sm text-content-muted">
                    {t('settings.roles.applyExplain', { count: role.memberCount })}
                  </p>
                  {applyDirty ? (
                    <p role="status" data-apply-blocked className="mt-2 text-sm text-danger">
                      {t('settings.roles.applySaveFirst')}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      value={applyText}
                      onChange={(event) => setApplyText(event.target.value)}
                      placeholder={t('settings.roles.applyTypeToConfirm', { name: role.name })}
                      aria-label={t('settings.roles.applyTypeToConfirm', { name: role.name })}
                      className={`${inputClass} w-64`}
                    />
                    <button
                      type="button"
                      disabled={!applyArmed || applying}
                      onClick={() => void handleApply()}
                      className={pillClass}
                    >
                      {t('settings.roles.applyButton', { count: role.memberCount })}
                    </button>
                  </div>
                </>
              )}
            </section>
          ) : null}
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

function Th({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <th className={`border-b border-subtle p-3 text-start font-medium ${className}`}>{children}</th>
  );
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`border-b border-subtle p-3 align-middle ${className}`}>{children}</td>;
}

function IconButton({
  children,
  label,
  onClick,
  danger,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={[
        'grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover',
        danger ? 'hover:text-danger' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
